import { Box, Button, Typography } from '@mui/material';
import Paper from 'components/paper';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import TextField from 'components/text-field';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { usePost } from 'hooks/use-post';
import React, { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';
import { Prompt } from 'react-router-dom';
import { ScenarioModel } from 'types/shared/scenario-model-type.enum';
import { getEnumValuesAsArray } from 'utils/enum';
import titleCase from 'utils/title-case';
import { putStepOne } from 'views/playground/features/scenario-planner-2.0/api/update-step-one';
import { useScenarioPlanState } from 'views/playground/features/scenario-planner-2.0/context/scenario-plan-context';
import { ScenarioPlan } from 'views/playground/features/scenario-planner-2.0/types/scenario-plan.type';
import { StepProps } from 'views/playground/features/scenario-planner-2.0/types/step-props.type';
import { UpdateStepOneInput } from 'views/playground/features/scenario-planner-2.0/types/update-step-one-input.type';

const StepOne: React.FC<StepProps> = ({ onClick }) => {
  const {
    scenarioPlanQuery: { data },
  } = useScenarioPlanState();
  const queryClient = useQueryClient();
  const snackbarDispatch = useSnackbarDispatch();

  const defaultValues = useMemo(() => {
    if (!!data?.stepOneFields) {
      return data.stepOneFields;
    }

    return {
      scenarioName: '',
      scenarioModel: ScenarioModel.EFFECTIVE_ENERGY_GT_2,
      calibration: '',
      generation: '',
    };
  }, [data]);

  const { control, handleSubmit, formState } = useForm<UpdateStepOneInput>({
    defaultValues: defaultValues, // step default form values
  });

  const updateStepOne = usePost<ScenarioPlan, UpdateStepOneInput>(
    (request) => putStepOne(request),
    (data) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: 'Success!' },
      });

      queryClient.invalidateQueries({
        queryKey: ['getPlaygroundScenarioPlan']
      });
    },
    (data) => {
      snackbarDispatch({
        type: SnackbarActionTypes.SUCCESS_SNACKBAR,
        payload: { msg: 'Fail!' },
      });
    }
  );

  const handleUpdate = (data: UpdateStepOneInput) => {
    updateStepOne.mutate(data, {
      onSuccess: () => {
        onClick(1); // only pass if successful update
      },
    });
  };

  const isFormDirty = useMemo(() => formState.isDirty, [formState]);

  return (
    <>
      <Prompt when={isFormDirty} message="You have unsaved changes. Are you sure you want to leave this page?" />
      <Paper
        sx={{
          height: '100%',
        }}>
        <form
          onSubmit={handleSubmit(handleUpdate)}
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            gap: 16,
          }}>
          <Typography>Overview</Typography>
          <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
            <TextField
              label={'Scenario Name'}
              dark
              fullWidth
              controller={{
                control,
                name: 'scenarioName',
                rules: {
                  required: true,
                },
              }}
            />
            <Box display={'flex'} flexDirection={'row'} gap={4}>
              <TextField
                label={'Generation'}
                dark
                fullWidth
                controller={{
                  control,
                  name: 'generation',
                  rules: {
                    required: true,
                  },
                }}
              />
              <Select
                label={'Model'}
                fullWidth
                dark
                controller={{
                  control,
                  name: 'scenarioModel',
                  rules: {
                    required: true,
                  },
                }}>
                {getEnumValuesAsArray(ScenarioModel).map((model: any) => (
                  <RadioSelectItem key={model} value={model}>
                    {titleCase(model)}
                  </RadioSelectItem>
                ))}
              </Select>
              <TextField
                label={'Calibration'}
                fullWidth
                dark
                controller={{
                  control,
                  name: 'calibration',
                  rules: {
                    required: true,
                  },
                }}
              />
            </Box>
          </Box>
          <Box display={'flex'} flexDirection={'row'} gap={2} justifyContent={'flex-end'}>
            <Button type="submit">Next</Button>
          </Box>
        </form>
      </Paper>
    </>
  );
};

export default StepOne;
